import type { BlogPostMeta } from "./types";

/** Fixed order defines cross-link ring (related posts use index offsets). */
export const SLUG_ORDER: string[] = [
  "top-5-inventory-loss-fmcg",
  "fefo-vs-fifo-practical-guide",
  "simplify-stocktaking-smb",
  "building-expirydesk-from-zero",
  "abc-xyz-classification-for-inventory",
  "safety-stock-service-levels-explained",
  "economic-order-quantity-basics",
  "inventory-turnover-days-on-hand",
  "designing-a-cycle-count-program",
  "perpetual-inventory-that-stays-accurate",
  "shrinkage-theft-damage-and-paper-gaps",
  "identifying-dead-stock-fast",
  "sku-rationalization-strategies",
  "warehouse-slotting-for-faster-picks",
  "pick-paths-and-batch-picking",
  "cross-docking-vs-staging-inventory",
  "vendor-managed-inventory-intro",
  "consignment-stock-and-cash-flow",
  "demand-forecasting-pitfalls",
  "seasonal-inventory-and-promo-stocks",
  "returns-and-reverse-logistics-stock",
  "planning-for-obsolescence",
  "batch-lot-traceability-requirements",
  "barcode-labeling-on-a-budget",
  "why-spreadsheets-break-at-scale",
  "upgrading-from-spreadsheets-to-systems",
  "stock-reconciliation-playbook",
  "inter-branch-stock-transfers",
  "inventory-accuracy-metrics-that-matter",
  "par-levels-min-max-reorder-points",
  "jit-versus-jic-stocking",
  "reducing-the-bullwhip-effect",
  "lean-5s-for-stockrooms",
  "stock-insurance-and-valuation-notes",
  "audit-ready-stock-records",
  "visibility-across-multiple-sites",
  "inventory-reporting-from-3pl-partners",
  "inbound-inspection-for-fewer-surprises",
  "improving-pick-pack-accuracy",
  "using-aging-reports-to-act",
  "negotiating-moq-and-order-cadence",
  "inventory-and-working-capital",
  "packaging-changes-and-data-hygiene",
  "disposal-donation-and-write-off-trails",
  "temperature-monitored-inventory-zones",
  "segregating-quarantine-and-hold-stock",
  "lead-time-variability-and-buffers",
  "slow-moving-obsolescence-risks",
  "ownership-culture-in-stock-teams",
  "resilience-and-strategic-safety-stock",
  "ai-stock-ordering-with-claude-excel-soh-movement",
  "how-much-food-does-the-average-family-waste-per-year",
  "how-many-people-take-the-wrong-medication-dose-every-day",
];

function relatedSlugsFor(index: number): string[] {
  const n = SLUG_ORDER.length;
  const offsets = [1, 7, 13, 19, 29];
  const out: string[] = [];
  for (const o of offsets) {
    const s = SLUG_ORDER[(index + o) % n];
    if (!out.includes(s)) out.push(s);
  }
  return out.slice(0, 5);
}

type Row = Omit<BlogPostMeta, "relatedSlugs">;

const ROWS: Row[] = [
  {
    slug: "top-5-inventory-loss-fmcg",
    title: "The Top 5 Causes of Inventory Loss in FMCG",
    description:
      "Spoilage, theft, data drift, supplier variability, and bad planning — why margin leaks and how operators close the gaps before audit season.",
    category: "FMCG",
    keywords: ["inventory loss", "FMCG shrinkage", "write-offs", "stock accuracy"],
    publishedAt: "2025-06-04",
    readingMinutes: 9,
  },
  {
    slug: "fefo-vs-fifo-practical-guide",
    title: "FEFO vs FIFO: A Practical Guide for Busy Teams",
    description:
      "When rotation order should follow expiry dates instead of arrival dates — and how to make either method auditable on the floor.",
    category: "Operations",
    keywords: ["FEFO", "FIFO", "rotation", "warehouse"],
    publishedAt: "2025-06-11",
    readingMinutes: 8,
  },
  {
    slug: "simplify-stocktaking-smb",
    title: "How to Simplify Stocktaking for SMBs Without Losing Control",
    description:
      "Zones, cycle samples, recount rules, and how finance gets numbers they can defend — without shutting the branch for days.",
    category: "Operations",
    keywords: ["stocktake", "cycle count", "SMB", "inventory audit"],
    publishedAt: "2025-06-18",
    readingMinutes: 8,
  },
  {
    slug: "building-expirydesk-from-zero",
    title: "Building ExpiryDesk: A Founder Build Log (Not a Pitch Deck)",
    description:
      "Why we bet on the web, what messy real-world sheets taught us, and how small reliability wins beat another empty dashboard.",
    category: "Founder",
    keywords: ["ExpiryDesk", "startup", "inventory software", "founder story"],
    publishedAt: "2025-06-25",
    readingMinutes: 7,
  },
  {
    slug: "abc-xyz-classification-for-inventory",
    title: "ABC–XYZ Classification: Prioritise the SKUs That Actually Move the Needle",
    description:
      "Segment SKUs by value and demand variability so counting effort, safety stock, and review cadence match real risk.",
    category: "Analytics",
    keywords: ["ABC analysis", "XYZ", "SKU segmentation", "inventory policy"],
    publishedAt: "2025-07-02",
    readingMinutes: 8,
  },
  {
    slug: "safety-stock-service-levels-explained",
    title: "Safety Stock and Service Levels in Plain Language",
    description:
      "Translate lead-time noise and demand swings into buffers that protect customers without inflating working capital forever.",
    category: "Analytics",
    keywords: ["safety stock", "service level", "lead time", "buffer"],
    publishedAt: "2025-07-09",
    readingMinutes: 9,
  },
  {
    slug: "economic-order-quantity-basics",
    title: "Economic Order Quantity (EOQ) Basics — When the Math Helps, When It Misleads",
    description:
      "Classic EOQ assumptions, where real warehouses break them, and how to pair formulas with floor feedback and supplier constraints.",
    category: "Supply chain",
    keywords: ["EOQ", "order quantity", "holding cost", "ordering cost"],
    publishedAt: "2025-07-16",
    readingMinutes: 8,
  },
  {
    slug: "inventory-turnover-days-on-hand",
    title: "Inventory Turnover and Days on Hand: Read the Signals, Not Just the Ratio",
    description:
      "High turnover can hide stock-outs; low turnover can hide margin. Pair velocity with service, margin, and expiry risk.",
    category: "Analytics",
    keywords: ["inventory turnover", "DOH", "days inventory", "KPIs"],
    publishedAt: "2025-07-23",
    readingMinutes: 8,
  },
  {
    slug: "designing-a-cycle-count-program",
    title: "Designing a Cycle Count Program That Finance Actually Trusts",
    description:
      "Cadence by ABC class, blind counts, tolerance rules, and root-cause codes — the difference between ticking boxes and fixing drift.",
    category: "Operations",
    keywords: ["cycle counting", "physical inventory", "accuracy"],
    publishedAt: "2025-07-30",
    readingMinutes: 9,
  },
  {
    slug: "perpetual-inventory-that-stays-accurate",
    title: "Making Perpetual Inventory Records Match the Floor",
    description:
      "Goods receipt, returns, transfers, and adjustments — the transaction hygiene that keeps your system from becoming fiction.",
    category: "Systems",
    keywords: ["perpetual inventory", "ERP", "stock records"],
    publishedAt: "2025-08-06",
    readingMinutes: 8,
  },
  {
    slug: "shrinkage-theft-damage-and-paper-gaps",
    title: "Shrinkage: Theft, Damage, and the Quiet Cost of Bad Data",
    description:
      "Separate honest handling loss from process gaps. Build variance codes so purchasing and ops each own a lever.",
    category: "Risk",
    keywords: ["shrinkage", "inventory loss", "variance"],
    publishedAt: "2025-08-13",
    readingMinutes: 8,
  },
  {
    slug: "identifying-dead-stock-fast",
    title: "How to Flag Dead Stock Before It Owns Your Shelves",
    description:
      "Aging thresholds, velocity tiers, and the meeting rhythm that turns “we should” into markdown, transfer, or supplier return.",
    category: "Retail",
    keywords: ["dead stock", "slow movers", "aging inventory"],
    publishedAt: "2025-08-20",
    readingMinutes: 8,
  },
  {
    slug: "sku-rationalization-strategies",
    title: "SKU Rationalisation: Fewer Codes, Clearer Operations",
    description:
      "When duplicate flavours of the same product inflate picks, errors, and safety stock — and how to sunset responsibly.",
    category: "Strategy",
    keywords: ["SKU rationalization", "assortment", "complexity"],
    publishedAt: "2025-08-27",
    readingMinutes: 8,
  },
  {
    slug: "warehouse-slotting-for-faster-picks",
    title: "Warehouse Slotting That Cuts Travel Time Without Chaos",
    description:
      "Fast-mover proximity, golden zone, and when to re-slot after promos — small layout wins that compound daily.",
    category: "Warehouse",
    keywords: ["slotting", "warehouse layout", "picking"],
    publishedAt: "2025-09-03",
    readingMinutes: 8,
  },
  {
    slug: "pick-paths-and-batch-picking",
    title: "Pick Paths and Batch Picking: Speed vs Accuracy Trade-offs",
    description:
      "Serpentine routes, zone picking, and batching rules that reduce touches without burying you in mis-picks.",
    category: "Warehouse",
    keywords: ["picking", "batch pick", "warehouse productivity"],
    publishedAt: "2025-09-10",
    readingMinutes: 8,
  },
  {
    slug: "cross-docking-vs-staging-inventory",
    title: "Cross-Docking vs Staging: Choosing the Right Flow",
    description:
      "When to push pallets straight to outbound, when staging protects quality, and how each choice hits inventory days.",
    category: "Warehouse",
    keywords: ["cross docking", "staging", "flow"],
    publishedAt: "2025-09-17",
    readingMinutes: 7,
  },
  {
    slug: "vendor-managed-inventory-intro",
    title: "Vendor-Managed Inventory (VMI): Where It Works, Where It Hurts",
    description:
      "Shared forecasts, min/max ownership, and the governance you need so VMI doesn’t become surprise invoices.",
    category: "Supply chain",
    keywords: ["VMI", "supplier collaboration", "replenishment"],
    publishedAt: "2025-09-24",
    readingMinutes: 8,
  },
  {
    slug: "consignment-stock-and-cash-flow",
    title: "Consignment Stock: Cash-Flow Upside and Control Risks",
    description:
      "Ownership transfer timing, counts, and reconciliation — so consignment stays a win, not a reconciliation swamp.",
    category: "Finance",
    keywords: ["consignment", "cash flow", "inventory ownership"],
    publishedAt: "2025-10-01",
    readingMinutes: 8,
  },
  {
    slug: "demand-forecasting-pitfalls",
    title: "Demand Forecasting Pitfalls That Inflate Inventory",
    description:
      "Promo spikes, one-off wins, and naive smoothing — how bad history poisons tomorrow’s buy unless you segment and clean.",
    category: "Analytics",
    keywords: ["forecasting", "demand planning", "bias"],
    publishedAt: "2025-10-08",
    readingMinutes: 9,
  },
  {
    slug: "seasonal-inventory-and-promo-stocks",
    title: "Seasonal Peaks and Promo Buffers Without Year-End Regret",
    description:
      "Pre-build discipline, exit dates, and post-mortems that stop holiday stock from becoming February liability.",
    category: "Retail",
    keywords: ["seasonality", "promotions", "buffer stock"],
    publishedAt: "2025-10-15",
    readingMinutes: 8,
  },
  {
    slug: "returns-and-reverse-logistics-stock",
    title: "Returns and Reverse Logistics: Keep Inventory Honest",
    description:
      "Quarantine paths, grading, and fast disposition — so returns don’t silently re-enter sellable stock as “maybe fine.”",
    category: "Operations",
    keywords: ["returns", "reverse logistics", "quarantine"],
    publishedAt: "2025-10-22",
    readingMinutes: 8,
  },
  {
    slug: "planning-for-obsolescence",
    title: "Planning for Obsolescence Before Marketing Moves On",
    description:
      "Lifecycle gates, last-buy discipline, and paired introduction/exit reviews for packaging and SKU changes.",
    category: "Strategy",
    keywords: ["obsolescence", "lifecycle", "SKU"],
    publishedAt: "2025-10-29",
    readingMinutes: 8,
  },
  {
    slug: "batch-lot-traceability-requirements",
    title: "Batch and Lot Traceability: Operational Essentials",
    description:
      "Receiving, storage, and issue rules that make recalls and audits a lookup exercise, not a week of archaeology.",
    category: "Compliance",
    keywords: ["lot traceability", "batch control", "recall readiness"],
    publishedAt: "2025-11-05",
    readingMinutes: 8,
  },
  {
    slug: "barcode-labeling-on-a-budget",
    title: "Barcode Labelling on a Budget: Start Simple, Scale Clean",
    description:
      "Symbology choices, print quality, and scan points that reduce wrong picks before you chase exotic automation.",
    category: "Systems",
    keywords: ["barcode", "labelling", "scanning"],
    publishedAt: "2025-11-12",
    readingMinutes: 7,
  },
  {
    slug: "why-spreadsheets-break-at-scale",
    title: "Why Spreadsheets Break When Inventory Gets Serious",
    description:
      "Concurrency, formula drift, and hidden rows — the structural limits that turn “temporary” workbooks into operational risk.",
    category: "Systems",
    keywords: ["spreadsheets", "inventory data", "risk"],
    publishedAt: "2025-11-19",
    readingMinutes: 7,
  },
  {
    slug: "upgrading-from-spreadsheets-to-systems",
    title: "Upgrading from Spreadsheets: A Pragmatic Path",
    description:
      "Pilot scope, master data cleanup, and the change management that gets floor adoption — without a six-month science project.",
    category: "Systems",
    keywords: ["inventory software", "digital transformation", "SMB"],
    publishedAt: "2025-11-26",
    readingMinutes: 9,
  },
  {
    slug: "stock-reconciliation-playbook",
    title: "Stock Reconciliation That Survives Month-End Scrutiny",
    description:
      "Cut-offs, unmatched receipts, and intercompany transfers — a playbook that ties the floor to the ledger on purpose.",
    category: "Finance",
    keywords: ["reconciliation", "inventory accuracy", "finance"],
    publishedAt: "2025-12-03",
    readingMinutes: 9,
  },
  {
    slug: "inter-branch-stock-transfers",
    title: "Inter-Branch Transfers: Visibility and Accountability",
    description:
      "In-transit status, ownership timing, and how partial shipments show up in both sites’ numbers.",
    category: "Operations",
    keywords: ["stock transfer", "multi-site", "visibility"],
    publishedAt: "2025-12-10",
    readingMinutes: 8,
  },
  {
    slug: "inventory-accuracy-metrics-that-matter",
    title: "Inventory Accuracy Metrics That Actually Change Behaviour",
    description:
      "Beyond “98% accurate” slogans — pick the few KPIs tied to customer fill rate, cash, and write-offs.",
    category: "Analytics",
    keywords: ["inventory accuracy", "KPIs", "metrics"],
    publishedAt: "2025-12-17",
    readingMinutes: 8,
  },
  {
    slug: "par-levels-min-max-reorder-points",
    title: "Par Levels, Min/Max, and Reorder Points: Set Them Without Guessing",
    description:
      "Demand intervals, lead-time spread, and review cadence — turning tribal knowledge into numbers new buyers can inherit.",
    category: "Operations",
    keywords: ["par level", "reorder point", "min max"],
    publishedAt: "2025-12-24",
    readingMinutes: 8,
  },
  {
    slug: "jit-versus-jic-stocking",
    title: "JIT vs JIC: Choosing the Right Posture for Your Supply Risk",
    description:
      "Just-in-time efficiency vs just-in-case resilience — segment by item criticality and supplier reliability, not ideology.",
    category: "Strategy",
    keywords: ["JIT", "JIC", "buffer strategy"],
    publishedAt: "2026-01-01",
    readingMinutes: 8,
  },
  {
    slug: "reducing-the-bullwhip-effect",
    title: "Taming the Bullwhip Effect in Your Supply Chain",
    description:
      "Order batching, forecast smoothing, and information sharing that stop small demand ripples becoming warehouse tsunamis.",
    category: "Supply chain",
    keywords: ["bullwhip effect", "supply chain", "variability"],
    publishedAt: "2026-01-08",
    readingMinutes: 8,
  },
  {
    slug: "lean-5s-for-stockrooms",
    title: "Lean 5S for Stockrooms: Discipline That Shows Up in Counts",
    description:
      "Sort, set, shine, standardise, sustain — the warehouse hygiene that makes expiry, damage, and mis-picks visible early.",
    category: "Operations",
    keywords: ["5S", "lean", "stockroom"],
    publishedAt: "2026-01-15",
    readingMinutes: 7,
  },
  {
    slug: "stock-insurance-and-valuation-notes",
    title: "Insurance, Valuation, and Inventory Records That Align",
    description:
      "Costing methods, coverage gaps after spoilage, and documentation habits that help when claims or audits arrive.",
    category: "Compliance",
    keywords: ["insurance", "inventory valuation", "risk"],
    publishedAt: "2026-01-22",
    readingMinutes: 8,
  },
  {
    slug: "audit-ready-stock-records",
    title: "Audit-Ready Inventory Records Without the Panic",
    description:
      "Immutable logs, segregation of duties, and traceable adjustments — the quiet infrastructure behind credible numbers.",
    category: "Compliance",
    keywords: ["audit", "inventory records", "controls"],
    publishedAt: "2026-01-29",
    readingMinutes: 8,
  },
  {
    slug: "visibility-across-multiple-sites",
    title: "Multi-Site Inventory Visibility: One Version of the Truth",
    description:
      "Hierarchy, transfers, and local policies — how to centralise visibility without crushing branch agility.",
    category: "Systems",
    keywords: ["multi-site", "visibility", "network inventory"],
    publishedAt: "2026-02-05",
    readingMinutes: 9,
  },
  {
    slug: "inventory-reporting-from-3pl-partners",
    title: "Getting Trustworthy Inventory Reporting from 3PL Partners",
    description:
      "SLA metrics, exception handling, and reconciliation cadence so outsourced warehousing doesn’t mean outsourced blind spots.",
    category: "Supply chain",
    keywords: ["3PL", "outsourced logistics", "reporting"],
    publishedAt: "2026-02-12",
    readingMinutes: 8,
  },
  {
    slug: "inbound-inspection-for-fewer-surprises",
    title: "Inbound Inspection That Prevents Surprise Stock Problems",
    description:
      "Sampling plans, damage photos, and ASN matching — catching supplier issues before bad stock pollutes your available balance.",
    category: "Warehouse",
    keywords: ["inbound", "receiving", "quality"],
    publishedAt: "2026-02-19",
    readingMinutes: 8,
  },
  {
    slug: "improving-pick-pack-accuracy",
    title: "Pick/Pack Accuracy: Error-Proofing Without Crushing Speed",
    description:
      "Scan-verify steps, weight checks, and simple visual cues that cut wrong-line shipments where they start.",
    category: "Warehouse",
    keywords: ["pick accuracy", "fulfilment", "quality"],
    publishedAt: "2026-02-26",
    readingMinutes: 8,
  },
  {
    slug: "using-aging-reports-to-act",
    title: "Aging Reports: Turn Lists Into Actions, Not Anxiety",
    description:
      "Buckets, owners, and weekly triage so aging dashboards drive markdowns and supplier talks — not shelf decoration.",
    category: "Analytics",
    keywords: ["aging report", "inventory ageing", "action"],
    publishedAt: "2026-03-05",
    readingMinutes: 8,
  },
  {
    slug: "negotiating-moq-and-order-cadence",
    title: "Negotiating MOQ and Order Cadence With Suppliers",
    description:
      "Volume breaks vs working capital, mixed pallets, and forecast visibility — levers beyond “please lower the MOQ.”",
    category: "Supply chain",
    keywords: ["MOQ", "supplier negotiation", "purchase policy"],
    publishedAt: "2026-03-12",
    readingMinutes: 8,
  },
  {
    slug: "inventory-and-working-capital",
    title: "Inventory and Working Capital: Speak the CFO’s Language",
    description:
      "Cash conversion, carrying cost, and the inventory levers that show up in board slides — not only in the stockroom.",
    category: "Finance",
    keywords: ["working capital", "cash", "inventory investment"],
    publishedAt: "2026-03-19",
    readingMinutes: 9,
  },
  {
    slug: "packaging-changes-and-data-hygiene",
    title: "Packaging Changes and SKU Hygiene: Avoid Silent Duplicates",
    description:
      "New barcodes, supersessions, and clean sunsets — so marketing’s “small refresh” doesn’t fork your stock records.",
    category: "Systems",
    keywords: ["SKU", "packaging", "master data"],
    publishedAt: "2026-03-26",
    readingMinutes: 7,
  },
  {
    slug: "disposal-donation-and-write-off-trails",
    title: "Disposal, Donation, and Write-Off Trails That Stand Up to Scrutiny",
    description:
      "Approvals, evidence, and inventory system entries that connect shrink events to policy — not to mystery.",
    category: "Compliance",
    keywords: ["write-off", "disposal", "donation", "controls"],
    publishedAt: "2026-04-02",
    readingMinutes: 8,
  },
  {
    slug: "temperature-monitored-inventory-zones",
    title: "Temperature-Monitored Zones: Basics for Sensitive Stock",
    description:
      "Mapping, alarms, and review rhythms — keeping climate-sensitive inventory inside spec without heroics.",
    category: "Warehouse",
    keywords: ["cold chain", "temperature monitoring", "storage"],
    publishedAt: "2026-04-09",
    readingMinutes: 8,
  },
  {
    slug: "segregating-quarantine-and-hold-stock",
    title: "Quarantine and Hold Stock: Physical Discipline, Clear Status",
    description:
      "Bins, labels, and system states so “do not sell” never depends on someone’s memory at the pick face.",
    category: "Warehouse",
    keywords: ["quarantine", "hold stock", "quality"],
    publishedAt: "2026-04-16",
    readingMinutes: 7,
  },
  {
    slug: "lead-time-variability-and-buffers",
    title: "Lead-Time Variability: Where Buffers Earn Their Keep",
    description:
      "Supplier reliability scores, confidence intervals, and when to invest in visibility instead of blind safety stock.",
    category: "Supply chain",
    keywords: ["lead time", "variability", "buffer"],
    publishedAt: "2026-04-23",
    readingMinutes: 8,
  },
  {
    slug: "slow-moving-obsolescence-risks",
    title: "Slow Movers and Obsolescence: Early Warnings Operators Miss",
    description:
      "Velocity decay, shelf life, and category-specific triggers — catching SLOB before it hardens into write-offs.",
    category: "Retail",
    keywords: ["slow moving", "SLOB", "obsolescence"],
    publishedAt: "2026-04-30",
    readingMinutes: 8,
  },
  {
    slug: "ownership-culture-in-stock-teams",
    title: "Ownership Culture in Stock Teams: From Blame to Metrics",
    description:
      "Clear RACI for counts, variances, and adjustments — so inventory accuracy is a habit, not a hero project.",
    category: "Strategy",
    keywords: ["culture", "accountability", "inventory governance"],
    publishedAt: "2026-05-07",
    readingMinutes: 7,
  },
  {
    slug: "resilience-and-strategic-safety-stock",
    title: "Resilience and Strategic Safety Stock After Disruption",
    description:
      "Scenario buffers, dual sourcing, and the data you need to unwind emergency stockpiles without whiplash.",
    category: "Strategy",
    keywords: ["resilience", "safety stock", "risk"],
    publishedAt: "2026-05-14",
    readingMinutes: 9,
  },
  {
    slug: "ai-stock-ordering-with-claude-excel-soh-movement",
    title: "AI Stock Ordering with Claude and Excel: SOH + 6-Month Movement",
    description:
      "Export stock on hand and recent item movement into one sheet, give Claude a structured prompt, and get suggested order quantities and runway from trend — before you buy another forecasting module.",
    category: "Supply chain",
    keywords: ["Claude", "Excel", "stock on hand", "reorder", "AI", "demand", "inventory planning"],
    publishedAt: "2026-04-21",
    readingMinutes: 10,
  },
  {
    slug: "how-much-food-does-the-average-family-waste-per-year",
    title: "How Much Food Does the Average Family Waste Per Year? (The Real Numbers)",
    description:
      "Household waste is measurable, not moralistic — what studies say, why dates and blind spots matter, and how visibility beats another guilt trip.",
    category: "Retail",
    keywords: ["food waste", "household", "expiry dates", "sustainability"],
    publishedAt: "2026-04-20",
    readingMinutes: 11,
  },
  {
    slug: "how-many-people-take-the-wrong-medication-dose-every-day",
    title: "How Many People Take the Wrong Medication Dose Every Day?",
    description:
      "Why dosing errors scale in absolute terms, who is most exposed, and how camera-assisted counts and dated stock reduce friction at home.",
    category: "Compliance",
    keywords: ["medication safety", "adherence", "polypharmacy", "pill count"],
    publishedAt: "2026-04-20",
    readingMinutes: 11,
  },
];

export const BLOG_POSTS: BlogPostMeta[] = ROWS.map((row, index) => ({
  ...row,
  relatedSlugs: relatedSlugsFor(index),
}));

export const postsBySlug: Record<string, BlogPostMeta> = Object.fromEntries(BLOG_POSTS.map((p) => [p.slug, p]));

export const allPostSlugs = SLUG_ORDER.filter((s) => postsBySlug[s]);

export function getPostMeta(slug: string): BlogPostMeta | null {
  return postsBySlug[slug] ?? null;
}
